import React from 'react';

/**
 * React.lazy that survives a deploy.
 *
 * A tab opened before a deploy asks for chunk files the deploy deleted, and
 * the server answers a missing file with index.html, so the import fails
 * ("Unexpected token '<'", "Loading chunk N failed", "Loading CSS chunk N
 * failed": Sentry NODE-EXPRESS-C / T) and the whole app falls to the error
 * screen. The fix for a stale tab is the new build: reload once.
 *
 * At most once per build: the flag records the build that reloaded, so a
 * chunk that is broken in the CURRENT build reloads once, fails again, and
 * reaches the error boundary instead of looping. A later deploy is a new
 * build and gets its own reload. (Clearing the flag after any successful load
 * would loop: other chunks load fine between the broken chunk's attempts.)
 */

const RELOAD_FLAG = 'fdev:chunk-reload';
const BUILD = process.env.REACT_APP_SENTRY_RELEASE || 'dev';

export function isChunkLoadError(err: unknown): boolean {
  const e = err as { name?: string; message?: string } | null;
  if (!e) return false;
  if (e.name === 'ChunkLoadError') return true;
  return /Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed|Unexpected token '<'/i.test(String(e.message || ''));
}

function reloadedThisBuild(): boolean | null {
  try { return window.sessionStorage.getItem(RELOAD_FLAG) === BUILD; } catch { return null; }
}

export function importWithReload<T>(load: () => Promise<T>): Promise<T> {
  return load().catch((err) => {
    if (isChunkLoadError(err) && reloadedThisBuild() === false) {
      try { window.sessionStorage.setItem(RELOAD_FLAG, BUILD); } catch { throw err; }
      window.location.reload();
      // Never settles: the page is going away, and rejecting would flash the
      // error screen before the reload lands.
      return new Promise<T>(() => {});
    }
    // Not a stale chunk, already reloaded for this build, or no storage to
    // guard a loop with: let the error boundary show it.
    throw err;
  });
}

export function lazyWithReload<T extends React.ComponentType<any>>(
  load: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return React.lazy(() => importWithReload(load));
}
